/**
 * Hint quality — the landing page.
 *
 * Three lenses on the same data, as tabs:
 *   - By question — where do hints get used, how deep, how rated. The place to
 *     start when improving the hints themselves.
 *   - Sessions    — what happened, newest first. The place to start when you
 *     want to read a particular student's session end to end.
 *   - By student  — who leans on hints, as a share of what they attempted.
 *
 * The active tab lives in the URL (`?view=`), so a link to either lens survives
 * a refresh and can be shared.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §7.
 */

import { useState } from 'react';
import { PageTabs, type PageTab } from '../components';
import { HintQualityQuestionsTab } from './HintQualityQuestionsTab';
import { HintQualitySessionsTab } from './HintQualitySessionsTab';
import { HintQualityStudentsTab } from './HintQualityStudentsTab';
import type { HintQualitySessionRow, HintQualityStudentRow } from './types';
import './HintQuality.css';

export type HintQualityView = 'questions' | 'sessions' | 'students';

const TABS: readonly PageTab<HintQualityView>[] = [
  { key: 'questions', label: 'By question' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'students', label: 'By student' },
];

const SUBTITLES: Record<HintQualityView, string> = {
  questions: 'Questions students have asked for hints on.',
  sessions: 'Individual hint sessions, most recent first.',
  students: 'How much each student leans on hints, against what they attempted.',
};

export interface HintQualitySummaryPageProps {
  /** Opens a question's drill-down (all its sessions). */
  onQuestionClick: (questionId: string) => void;
  /** Opens one session's detail. Given the whole row so the host can route by
   *  question and still say which session to open. */
  onSessionClick: (row: HintQualitySessionRow) => void;
  /** Active tab, owned by the host so it can live in the URL. */
  view: HintQualityView;
  onViewChange: (view: HintQualityView) => void;
}

export function HintQualitySummaryPage({
  onQuestionClick, onSessionClick, view, onViewChange,
}: HintQualitySummaryPageProps) {
  // Seeded when arriving from a student row; the Sessions tab owns it after that.
  const [sessionSearch, setSessionSearch] = useState<string | undefined>();

  return (
    <div className="hintq-page">
      <PageTabs
        title="Hint Quality"
        subtitle={SUBTITLES[view]}
        tabs={TABS}
        active={view}
        onChange={onViewChange}
      />

      {view === 'questions' && <HintQualityQuestionsTab onQuestionClick={onQuestionClick} />}
      {view === 'sessions' && (
        <HintQualitySessionsTab onSessionClick={onSessionClick} initialSearch={sessionSearch} />
      )}
      {view === 'students' && (
        // Clicking a student jumps to Sessions pre-filtered to them. Reuses the
        // free-text search (which covers email) rather than adding a second
        // student-scoping mechanism that could drift from it.
        <HintQualityStudentsTab
          onStudentClick={(r: HintQualityStudentRow) => {
            setSessionSearch(r.email || r.name);
            onViewChange('sessions');
          }}
        />
      )}
    </div>
  );
}
